
/**
 * Utility functions for image conversion operations
 */

/**
 * Converts an image file to WebP format
 * @param file The original image file
 * @returns A Promise that resolves to a new File object in WebP format
 */
export const convertToWebP = async (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      
      // Add error handling with detailed logging
      img.onerror = (e) => {
        console.error('Failed to load image in convertToWebP:', e);
        // If the image fails to load, just return the original file
        // This is a fallback to prevent complete failure
        resolve(file);
      };
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            console.warn('Failed to get canvas context, returning original file');
            resolve(file);
            return;
          }
          
          // Draw the image on the canvas
          ctx.drawImage(img, 0, 0);
          
          // Convert to WebP
          canvas.toBlob((blob) => {
            if (!blob) {
              console.warn('Failed to convert image to WebP, returning original file');
              resolve(file);
              return;
            }
            
            // Create a new File object with the WebP blob
            const webpFile = new File([blob], `${file.name.split('.')[0]}.webp`, {
              type: 'image/webp',
              lastModified: Date.now()
            });
            
            resolve(webpFile);
          }, 'image/webp', 0.85); // 0.85 is a good quality/size balance
        } catch (error) {
          console.error('Error in convertToWebP canvas operations:', error);
          // Return original file as fallback
          resolve(file);
        }
      };
      
      // Set the image source with a timeout for safety
      const dataUrl = event.target?.result as string;
      if (dataUrl) {
        img.src = dataUrl;
      } else {
        console.error('Failed to get data URL from FileReader');
        resolve(file);
      }
    };
    
    reader.onerror = (error) => {
      console.error('FileReader error in convertToWebP:', error);
      resolve(file); // Return original as fallback
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Converts a data URL to a File object
 * @param dataUrl The data URL string
 * @param filename The filename to use
 * @returns A File object
 */
export const dataUrlToFile = (dataUrl: string, filename: string): File => {
  // Extract the MIME type from the data URL
  const mime = dataUrl.match(/data:([^;]+)/)![1];
  
  // Convert base64 to blob
  const byteString = atob(dataUrl.split(',')[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  
  const blob = new Blob([ab], { type: mime });
  return new File([blob], filename, { type: mime });
};
